import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  BookmarkBorder,
  Bookmark,
  Share,
} from '@mui/icons-material';
import FireEmojiSlider from '../Rating/FireEmojiSlider';

interface QuickActionOverlayProps {
  outfitId: string;
  onRate: (rating: number) => void;
  onLike: () => void;
  onSave: () => void;
  onShare: () => void;
  visible: boolean;
  isLiked?: boolean;
  isSaved?: boolean;
  currentRating?: number;
  disabled?: boolean;
}

/**
 * QuickActionOverlay — glassmorphism overlay that appears on outfit cards
 * with like, rate (fire emoji slider), save, and share actions.
 */
const QuickActionOverlay: React.FC<QuickActionOverlayProps> = ({
  outfitId,
  onRate,
  onLike,
  onSave,
  onShare,
  visible,
  isLiked = false,
  isSaved = false,
  currentRating = 0,
  disabled = false,
}) => {
  const [localRating, setLocalRating] = useState(currentRating);

  const handleRate = (value: number) => {
    setLocalRating(value);
    onRate(value);
  };

  return (
    <Box
      data-testid={`quick-action-overlay-${outfitId}`}
      onClick={(e) => {
        // Only stop propagation if the click target is an interactive element
        // (button, slider, etc.) — let background clicks pass through to CardActionArea
        const target = e.target as HTMLElement;
        const isInteractive = target.closest('button, [role="slider"], .MuiIconButton-root, .MuiSlider-root');
        if (visible && isInteractive) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        const isInteractive = target.closest('button, [role="slider"], .MuiIconButton-root, .MuiSlider-root');
        if (visible && isInteractive) e.stopPropagation();
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        pb: 2,
        px: 1.5,
        gap: 1,
        // Semi-transparent gradient from transparent at top to dark at bottom
        // NO backdrop-filter blur — this was causing the entire image to go fuzzy
        background: visible
          ? 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 40%, transparent 70%)'
          : 'transparent',
        borderRadius: 'inherit',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 200ms ease',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      {/* Fire emoji rating slider */}
      <FireEmojiSlider
        value={localRating}
        onChange={handleRate}
        disabled={disabled}
        size="medium"
      />

      {/* Action buttons row */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Tooltip title={isLiked ? 'Unlike' : 'Like'}>
          <IconButton
            onClick={onLike}
            disabled={disabled}
            aria-label={isLiked ? 'Unlike this outfit' : 'Like this outfit'}
            sx={{ color: isLiked ? 'error.main' : 'text.primary' }}
          >
            {isLiked ? <Favorite /> : <FavoriteBorder />}
          </IconButton>
        </Tooltip>

        <Tooltip title={isSaved ? 'Unsave' : 'Save'}>
          <IconButton
            onClick={onSave}
            disabled={disabled}
            aria-label={isSaved ? 'Remove from saved' : 'Save this outfit'}
            sx={{ color: isSaved ? 'warning.main' : 'text.primary' }}
          >
            {isSaved ? <Bookmark /> : <BookmarkBorder />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Share">
          <IconButton
            onClick={onShare}
            disabled={disabled}
            aria-label="Share this outfit"
            sx={{ color: 'text.primary' }}
          >
            <Share />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default QuickActionOverlay;
